import styled from '@emotion/styled';
import { useMemo } from 'react';

import { type TransactionType, TransactionTypes } from '@/api/fund/types';
import SearchIcon from '@/assets/svg/ui-elements/search-refraction.svg';
import type { TransactionListFilters } from '@/features/transactions/utils/transaction-list';
import {
  Button,
  DatePicker,
  forMobile,
  Input,
  Select,
  type SelectOption,
  Spinner,
} from '@/shared/design-system';

interface TransactionFiltersProps {
  filters: TransactionListFilters;
  directionOptions: SelectOption[];
  denominationOptions: SelectOption[];
  typeOptions: SelectOption[];
  onFiltersChange: (_filters: TransactionListFilters) => void;
  onRefresh?: () => Promise<unknown> | void;
  isRefreshing: boolean;
}

const ALL_DIRECTIONS = '__all-directions__';
const ALL_DENOMINATIONS = '__all-denominations__';
const ALL_TYPES = '__all-types__';

const isTransactionType = (value: string): value is TransactionType =>
  Object.values(TransactionTypes).some((type) => type === value);

const safelyRefresh = async (onRefresh: () => Promise<unknown> | void) => {
  try {
    await onRefresh();
  } catch {
    return;
  }
};

const FilterBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[16],
}));

const SearchControl = styled('div')({
  minWidth: 600,
  [forMobile]: {
    minWidth: '100%',
  },
});

const FilterControl = styled('div')({
  flex: '0 0 auto',
  width: 'max-content',
});

const DateControl = styled(FilterControl)({
  width: 184,
  '& [role="button"] > span:last-child': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const DirectionControl = styled(FilterControl)({
  minWidth: 120,
});

const DenominationControl = styled(FilterControl)({
  minWidth: 157,
});

const TypeControl = styled(FilterControl)({
  minWidth: 90,
});

const RefreshControl = styled('div')({
  alignSelf: 'flex-start',
  flexShrink: 0,
  marginLeft: 'auto',
});

const RefreshButton = styled(Button)({
  minWidth: 128,
});

export const TransactionFilters = ({
  filters,
  directionOptions,
  denominationOptions,
  typeOptions,
  onFiltersChange,
  onRefresh,
  isRefreshing,
}: TransactionFiltersProps) => {
  const today = useMemo(() => new Date(), []);
  const updateFilters = (updates: Partial<TransactionListFilters>) =>
    onFiltersChange({ ...filters, ...updates });

  return (
    <FilterBar>
      <SearchControl>
        <Input
          size="lg"
          aria-label="Search transactions"
          placeholder="Search"
          value={filters.searchTerm}
          onChange={(event) => updateFilters({ searchTerm: event.target.value })}
          startIcon={<SearchIcon aria-hidden="true" />}
        />
      </SearchControl>

      <DateControl>
        <DatePicker
          months="range"
          fullWidth
          placeholder="Date"
          maxDate={today}
          value={{ from: filters.dateRange[0], to: filters.dateRange[1] }}
          onChange={({ from, to }) => updateFilters({ dateRange: [from, to] })}
        />
      </DateControl>

      <DirectionControl>
        <Select
          size="lg"
          ariaLabel="Filter by direction"
          placeholder="Direction"
          triggerLabel="Direction"
          value={filters.direction || null}
          options={[{ value: ALL_DIRECTIONS, label: 'All Directions' }, ...directionOptions]}
          onChange={(value) => updateFilters({ direction: value === ALL_DIRECTIONS ? '' : value })}
        />
      </DirectionControl>

      <DenominationControl>
        <Select
          size="lg"
          ariaLabel="Filter by denomination"
          placeholder="Denomination"
          triggerLabel="Denomination"
          value={filters.denomination || null}
          options={[
            { value: ALL_DENOMINATIONS, label: 'All Denominations' },
            ...denominationOptions,
          ]}
          onChange={(value) =>
            updateFilters({ denomination: value === ALL_DENOMINATIONS ? '' : value })
          }
        />
      </DenominationControl>

      <TypeControl>
        <Select
          size="lg"
          ariaLabel="Filter by transaction type"
          placeholder="Type"
          triggerLabel="Type"
          value={filters.type || null}
          options={[{ value: ALL_TYPES, label: 'All Types' }, ...typeOptions]}
          onChange={(value) => {
            if (value === ALL_TYPES) return updateFilters({ type: '' });
            if (isTransactionType(value)) updateFilters({ type: value });
          }}
        />
      </TypeControl>

      {onRefresh ? (
        <RefreshControl>
          <RefreshButton
            size="md"
            variant="subtle"
            color="secondary"
            disabled={isRefreshing}
            aria-busy={isRefreshing}
            onClick={() => void safelyRefresh(onRefresh)}
          >
            {isRefreshing ? (
              <>
                Refreshing…
                <Spinner color="secondary" />
              </>
            ) : (
              'Refresh'
            )}
          </RefreshButton>
        </RefreshControl>
      ) : null}
    </FilterBar>
  );
};
